import { useId, useState, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import useIsPWA from '@/hooks/use-is-pwa';
import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface TimePickerProps {
    name: string;
    label?: ReactNode;
    required?: boolean;
    error?: FieldErrorLike;
    InputLabelProps?: LabelHTMLAttributes<HTMLLabelElement>;
    custom?: InputHTMLAttributes<HTMLInputElement>;
}

/** Saisie d'une heure, enregistrée dans le formulaire react-hook-form parent. */
const TimePicker = ({ required, label, InputLabelProps, custom, error, name }: TimePickerProps) => {
    const {
        register,
        watch,
        formState: { errors },
    } = useFormContext();
    const registration = register(name);
    const value = watch(name) as string | undefined;
    const [focused, setFocused] = useState(false);
    const isPWA = useIsPWA();
    const id = useId();

    const isActive = focused || Boolean(value);
    const displayError = errorMessage(error) || (errors[name]?.message as string | undefined);

    return (
        <div className="relative w-full pwa-time-wrapper">
            <input
                id={id}
                type="time"
                className={`peer w-full px-6 py-4 rounded-lg border border-border bg-transparent text-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${displayError ? 'border-destructive focus:ring-destructive/20 focus:border-destructive' : ''} ${!isActive ? 'text-transparent' : ''}`}
                {...registration}
                {...custom}
                onFocus={() => setFocused(true)}
                onBlur={(e) => {
                    void registration.onBlur(e);
                    setFocused(false);
                }}
                value={value || ''}
            />
            {label && (
                <label
                    {...InputLabelProps}
                    htmlFor={id}
                    className={`absolute left-3 ${isPWA ? (isActive ? 'bg-white' : 'bg-transparent') : 'bg-card'} px-1 text-muted-foreground transition-all duration-200 pointer-events-none ${isActive ? 'text-xs -top-2 text-primary' : 'text-sm top-4'} ${displayError ? 'text-destructive' : ''}`}
                >
                    {label}
                    {required ? (
                        <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                            *
                        </sup>
                    ) : (
                        ''
                    )}
                </label>
            )}
            {displayError && <p className="mt-1 text-xs text-destructive italic">{displayError}</p>}
        </div>
    );
};

export default TimePicker;
